import { Save, Upload, X } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { setGlobalProductPopupElement } from "../features/popupSlice";
import { CustomHeaderItem } from "../types/header";
import { getImageUrl } from "../utils";
import ImagePopUpV3 from "./ImagePopUpV3";

interface SaveHeaderModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (headerData: any) => Promise<CustomHeaderItem>;
    header?: CustomHeaderItem; // Existing header data for editing, if any
    isEdit?: boolean;
}

export const SaveHeaderModal: React.FC<SaveHeaderModalProps> = ({
    isOpen,
    onClose,
    onSave,
    header,
    isEdit = false,
}) => {
    const dispatch = useDispatch();

    // Form state
    const [name, setName] = useState<string>(header?.name || "");
    const [image, setImage] = useState<string>(
        header?.images || "https://placehold.co/1200x300"
    );
    const [description, setDescription] = useState<string>(
        header?.description || ""
    );
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [errors, setErrors] = useState<{ [key: string]: string }>({});

    // Set initial form values when editing
    useEffect(() => {
        if (header) {
            setName(header.name);
            setImage(header.images);
            setDescription(header.description || "");
        } else {
            // Reset form for new header
            setName("");
            setImage("https://placehold.co/1200x300");
            setDescription("");
        }
        // Reset errors when opening modal
        setErrors({});
    }, [header, isOpen]);

    // Validate form
    const validateForm = (): boolean => {
        const newErrors: { [key: string]: string } = {};

        if (!name.trim()) {
            newErrors.name = "Header name is required";
        }

        if (!image) {
            newErrors.image = "Header image is required";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Handle form submission
    const handleSubmit = async (e: React.FormEvent): Promise<void> => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setIsSaving(true);

        try {
            const headerData: CustomHeaderItem = {
                ...(header?._id ? { id: header._id } : {}),
                name,
                image,
                description,
            } as any;

            await onSave(headerData);
            onClose();
        } catch (error) {
            console.error("Error saving header:", error);
            setErrors({
                submit: "Failed to save header. Please try again.",
            });
        } finally {
            setIsSaving(false);
        }
    };

    // Handle image upload (mock)
    const handleUpload = () => {
        dispatch(
            setGlobalProductPopupElement(
                <ImagePopUpV3
                    onClose={() => {
                        dispatch(setGlobalProductPopupElement(null));
                    }}
                    onUpdate={(images) => {
                        setImage(images?.[0]?.path || "");
                        dispatch(setGlobalProductPopupElement(null));
                    }}
                    single
                />
            )
        );
    };

    // If modal is closed, don't render
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
                {/* Modal header */}
                <div className="flex items-center justify-between p-4 border-b">
                    <h2 className="text-xl font-semibold">
                        {isEdit ? "Edit Header" : "Create New Header"}
                    </h2>
                    <button
                        onClick={onClose}
                        className="p-1 rounded-full hover:bg-gray-100"
                    >
                        <X className="w-6 h-6" />
                    </button>
                </div>

                {/* Modal body - Form */}
                <form onSubmit={handleSubmit} style={{ display: "block" }}>
                    <div className="p-4 space-y-4">
                        {/* Name field */}
                        <div>
                            <label
                                htmlFor="header-name"
                                className="block text-sm font-medium text-gray-700 mb-1"
                            >
                                Header Name{" "}
                                <span className="text-red-500">*</span>
                            </label>
                            <input
                                id="header-name"
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                className={`w-full p-2 border rounded-md ${
                                    errors.name
                                        ? "border-red-500"
                                        : "border-gray-300"
                                }`}
                                placeholder="Enter header name"
                            />
                            {errors.name && (
                                <p className="mt-1 text-sm text-red-500">
                                    {errors.name}
                                </p>
                            )}
                        </div>

                        {/* Image upload */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Header Image{" "}
                                <span className="text-red-500">*</span>
                            </label>
                            <div className="border rounded-md overflow-hidden">
                                {/* Image preview */}
                                <button
                                    type="button"
                                    onClick={handleUpload}
                                    className="w-full h-24 bg-gray-100 relative"
                                >
                                    {image && (
                                        <img
                                            src={
                                                image
                                                    ? getImageUrl(image)
                                                    : "https://placehold.co/1200x300"
                                            }
                                            alt="Header"
                                            className="object-cover w-full h-full"
                                        />
                                    )}

                                    {/* Upload overlay */}
                                    <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                                        <label
                                            htmlFor="image-upload"
                                            className="bg-white p-2 rounded-md cursor-pointer flex items-center"
                                        >
                                            <Upload className="w-4 h-4 mr-1" />
                                            <span className="text-sm">
                                                Change Image
                                            </span>
                                        </label>
                                    </div>
                                </button>
                            </div>
                            {errors.image && (
                                <p className="mt-1 text-sm text-red-500">
                                    {errors.image}
                                </p>
                            )}
                            <p className="mt-1 text-xs text-gray-500">
                                Recommended size: 1200×300px. Max file size:
                                2MB.
                            </p>
                        </div>

                        {/* Description field */}
                        <div>
                            <label
                                htmlFor="header-description"
                                className="block text-sm font-medium text-gray-700 mb-1"
                            >
                                Description
                            </label>
                            <textarea
                                id="header-description"
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                className="w-full p-2 border border-gray-300 rounded-md"
                                rows={3}
                                placeholder="Brief description of this header"
                            />
                        </div>

                        {/* General error message */}
                        {errors.submit && (
                            <div className="p-2 bg-red-50 border border-red-200 rounded-md">
                                <p className="text-sm text-red-600">
                                    {errors.submit}
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Modal footer */}
                    <div className="p-4 border-t flex justify-end space-x-3">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 border border-gray-300 rounded-md bg-white hover:bg-gray-50 text-gray-700"
                            disabled={isSaving}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md flex items-center"
                            disabled={isSaving}
                        >
                            <Save className="w-4 h-4 mr-2" />
                            {isSaving ? "Saving..." : "Save Header"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// Usage example component
const SaveHeaderModalExample: React.FC = () => {
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [isEditMode, setIsEditMode] = useState<boolean>(false);
    const [currentHeader, setCurrentHeader] = useState<
        CustomHeaderItem | undefined
    >(undefined);

    const handleOpenNewModal = (): void => {
        setIsEditMode(false);
        setCurrentHeader(undefined);
        setIsModalOpen(true);
    };

    const handleOpenEditModal = (): void => {
        setIsEditMode(true);
        // setCurrentHeader(exampleHeader);
        setIsModalOpen(true);
    };

    const handleSaveHeader = async (
        headerData: any
    ): Promise<CustomHeaderItem> => {
        // Simulate API call
        return new Promise((resolve) => {
            setTimeout(() => {
                console.log("Saved header data:", headerData);
                resolve(headerData);
            }, 1000);
        });
    };

    return (
        <div className="p-4 space-y-4">
            <button
                onClick={handleOpenNewModal}
                className="px-4 py-2 bg-blue-600 text-white rounded mr-4"
            >
                Create New Header
            </button>

            <button
                onClick={handleOpenEditModal}
                className="px-4 py-2 bg-green-600 text-white rounded"
            >
                Edit Existing Header
            </button>

            <SaveHeaderModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSave={handleSaveHeader}
                header={currentHeader}
                isEdit={isEditMode}
            />
        </div>
    );
};

export default SaveHeaderModalExample;
